import React, { useState, useEffect } from "react";
import { useApp } from "../context/AppContext";
import { User, Message } from "../types";
import { API_ENDPOINTS } from "../config";
import Chat from "./features/Chat";
import ChatBox from "./features/ChatBox";
import defaultAvatar from "../assets/avatar.png";

const MainScreen: React.FC = () => {
  const { state, initiateCall, logout, setConversationId, dispatch } = useApp();
  const [onlineUsers, setOnlineUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chatStarted, setChatStarted] = useState(false);
  const [initialMessage, setInitialMessage] = useState("");
  const [existingMessages, setExistingMessages] = useState<Message[]>([]);
  const [existingProviders, setExistingProviders] = useState<any[]>([]);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Note: We do NOT clear conversationId on page unload
  // because we want to restore the conversation when returning from payment

  // Track chatStarted state changes
  useEffect(() => {
    console.log("[MainScreen] chatStarted changed to:", chatStarted);
  }, [chatStarted]);

  // Restore conversation on mount if conversationId exists in sessionStorage
  useEffect(() => {
    const restoreConversation = async () => {
      console.log("[MainScreen] Restoration check triggered");
      const conversationId = sessionStorage.getItem('conversationId');
      const returningFromPayment = sessionStorage.getItem('returningFromPayment');

      console.log("[MainScreen] conversationId:", conversationId, "returningFromPayment:", returningFromPayment);
      console.log("[MainScreen] chatStarted state:", chatStarted);

      // Restore conversation if:
      // 1. conversationId exists, AND
      // 2. Either chat hasn't started OR we're returning from payment
      const shouldRestore = conversationId && (!chatStarted || returningFromPayment === 'true');

      // Clear the returning flag after checking it
      if (returningFromPayment === 'true') {
        console.log("[MainScreen] Clearing returningFromPayment flag");
        sessionStorage.removeItem('returningFromPayment');
      }

      if (shouldRestore) {
        console.log("[MainScreen] Attempting to restore conversation:", conversationId);

        try {
          const token = localStorage.getItem("token");
          if (!token) {
            console.log("[MainScreen] No token found, cannot restore conversation");
            return;
          }

          const response = await fetch(
            `${API_ENDPOINTS.CONVERSATION}?conversation_id=${conversationId}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!response.ok) {
            console.error("[MainScreen] Failed to fetch conversation");
            return;
          }

          const data = await response.json();
          console.log("[MainScreen] Conversation data loaded:", data);

          if (data.messages && data.messages.length > 0) {
            // Find the last message with user_list (providers)
            let latestProviders: any[] = [];
            for (let i = data.messages.length - 1; i >= 0; i--) {
              const msg = data.messages[i];
              if (msg.role === "assistant" && msg.context?.user_list && msg.context.user_list.length > 0) {
                latestProviders = msg.context.user_list;
                console.log(`[MainScreen] Found ${latestProviders.length} providers in message ${i}`);
                break;
              }
            }

            // Format messages
            const formattedMessages: Message[] = data.messages
              .map((msg: any, index: number) => {
                console.log(`[MainScreen] Processing message ${index}:`, {
                  role: msg.role,
                  hasContext: !!msg.context,
                  contextKeys: msg.context ? Object.keys(msg.context) : [],
                  question: msg.context?.question,
                  answer: msg.context?.answer,
                  hasUserList: !!msg.context?.user_list
                });

                let content = "";

                if (msg.role === "user") {
                  content = msg.context.question;
                } else {
                  // For assistant messages, use answer field
                  content = msg.context.answer;

                  // If answer is empty but user_list exists, create a message
                  if (!content && msg.context.user_list && msg.context.user_list.length > 0) {
                    const count = msg.context.user_list.length;
                    content = `I found ${count} therapist${count > 1 ? 's' : ''} that match your needs. You can view their profiles in the panel on the right.`;
                    console.log(`[MainScreen] Generated default message for empty answer with ${count} providers`);
                  }
                }

                console.log(`[MainScreen] Message ${index} content:`, content);

                if (content) {
                  return {
                    id: `restored-${conversationId}-${index}`,
                    content: content,
                    isUser: msg.role === "user",
                    timestamp: new Date(),
                  };
                }
                console.log(`[MainScreen] Message ${index} skipped - no content`);
                return null;
              })
              .filter((msg: Message | null) => msg !== null);

            console.log("[MainScreen] Formatted messages:", formattedMessages.length);
            console.log("[MainScreen] Formatted messages details:", formattedMessages);

            if (formattedMessages.length > 0) {
              console.log("[MainScreen] Restoring chat with messages");
              setExistingMessages(formattedMessages);

              if (latestProviders.length > 0) {
                console.log("[MainScreen] Restoring providers panel");
                setExistingProviders(latestProviders);
              }

              setChatStarted(true);
            }
          }
        } catch (error) {
          console.error("[MainScreen] Error restoring conversation:", error);
        }
      } else if (!conversationId) {
        console.log("[MainScreen] No conversationId found, starting fresh");
      } else if (chatStarted) {
        console.log("[MainScreen] Chat already started, skipping restoration");
      }
    };

    restoreConversation();

    // Listen for custom restoration event from PaymentSuccess
    const handleRestoreEvent = () => {
      console.log("[MainScreen] restoreConversation event received");
      restoreConversation();
    };

    window.addEventListener('restoreConversation', handleRestoreEvent);

    return () => {
      console.log("[MainScreen] Component unmounting");
      window.removeEventListener('restoreConversation', handleRestoreEvent);
    };
  }, [state.screen]); // Re-run when screen changes (e.g., returning from payment)

  // Get profile photo URL or use default avatar
  const getProfilePhotoUrl = () => {
    if (state.user?.profile_photo_url) {
      const apiBaseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
      return `${apiBaseURL}${state.user.profile_photo_url}`;
    }
    return defaultAvatar;
  };

  const currentUser = {
    name: state.user?.name || "unknownUser",
    avatar: getProfilePhotoUrl(),
    status: "online",
  };

  // Fetch online users list
  const fetchOnlineUsers = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = localStorage.getItem("token");
      const response = await fetch(API_ENDPOINTS.USERS, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch online users");
      }

      const data = await response.json();
      // Filter out current user
      const filteredUsers = data.filter(
        (user: User) => user.id !== state.currentUserId
      );
      setOnlineUsers(filteredUsers);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch online users"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch user list on component mount
  useEffect(() => {
    fetchOnlineUsers();
    // Set periodic refresh
    const interval = setInterval(fetchOnlineUsers, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const handleCall = async (userId: string) => {
    try {
      await initiateCall(userId);
    } catch (error) {
      console.error("Failed to initiate call:", error);
    }
  };

  const handleConversationIdChange = (conversationId: string) => {
    setConversationId(conversationId);
  };

  const getUserTypeLabel = (type: string) => {
    switch (type) {
      case "therapist":
        return "Therapist";
      default:
        return "Regular User";
    }
  };

  const handleStartChat = (message: string, messages?: Message[]) => {
    console.log("[MainScreen] handleStartChat called - message:", message, "messages count:", messages?.length || 0);
    setInitialMessage(message);
    if (messages) {
      console.log("[MainScreen] Setting existing messages:", messages.length);
      setExistingMessages(messages);
    }
    console.log("[MainScreen] Setting chatStarted to true");
    setChatStarted(true);
  };

  const handleNewChat = () => {
    sessionStorage.removeItem("conversationId");
    setChatStarted(false);
    setInitialMessage("");
    setExistingMessages([]);
  };

  const handleProfilePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Invalid file type. Please upload JPG, PNG, GIF, or WEBP images only.');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('File too large. Maximum size is 5MB.');
      return;
    }

    setIsUploadingPhoto(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('photo', file);

      const token = localStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.UPLOAD_PROFILE_PHOTO, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload profile photo');
      }

      const data = await response.json();

      // Update profile photo in AppContext state
      dispatch({ type: 'UPDATE_PROFILE_PHOTO', payload: data.photo_url });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Failed to upload profile photo');
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const triggerFileInput = () => {
    document.getElementById('profile-photo-input')?.click();
  };

  return (
    <div className="main-screen-container">
      <div className="sidebar">
        <div className="user-profile">
          <div className="user-avatar" onClick={triggerFileInput} style={{ cursor: 'pointer', position: 'relative' }}>
            <img src={currentUser.avatar} alt={currentUser.name} />
            <span className="status-indicator"></span>
            {isUploadingPhoto && (
              <div className="upload-overlay">
                Uploading...
              </div>
            )}
            <div className="camera-icon">
              📷
            </div>
          </div>
          <input
            id="profile-photo-input"
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            onChange={handleProfilePhotoChange}
            style={{ display: 'none' }}
          />
          <h3>{currentUser.name}</h3>
          {uploadError && (
            <p className="upload-error">{uploadError}</p>
          )}
          <button
            className="payment-history-button"
            onClick={() => {
              console.log("[MainScreen] Navigating to payment history, conversationId:", sessionStorage.getItem('conversationId'));
              window.history.pushState({}, "", "/payment/history");
              window.dispatchEvent(new PopStateEvent("popstate"));
            }}
          >
            Payment History
          </button>
          <button className="logout-button" onClick={logout}>
            Logout
          </button>
        </div>

        {/* <div className="sidebar-actions">
          <button className="new-chat-button" onClick={handleNewChat}>
            <span className="icon">+</span>
            New Chat
          </button>
        </div>

        <div className="conversations-list">
          {isLoading && <p className="loading-text">Loading...</p>}
          {error && <p className="error-message">{error}</p>}
          {!isLoading && onlineUsers.length === 0 && (
            <p className="no-users">No active conversations</p>
          )}
          <div className="users-list">
            {onlineUsers.map((user) => (
              <div key={user.id} className="user-card">
                <div className="user-info">
                  <p className="user-email">{user.email}</p>
                  <p className="user-type">{getUserTypeLabel(user.type)}</p>
                  <p className="user-status">
                    {user.inCallWith ? "In Call" : "Online"}
                  </p>
                </div>
                <button
                  className="call-button"
                  onClick={() => handleCall(user.id)}
                  disabled={!!user.inCallWith}
                >
                  {user.inCallWith ? "In Call" : "📞 Call"}
                </button>
              </div>
            ))}
          </div>
        </div> */}
      </div>
      <div className="main-content">
        {!chatStarted ? (
          <Chat onStartChat={handleStartChat} />
        ) : (
          <ChatBox
            initialMessage={initialMessage}
            existingMessages={existingMessages}
            existingProviders={existingProviders}
            onConversationIdChange={handleConversationIdChange}
          />
        )}
      </div>

      {state.error && <div className="error-message">{state.error}</div>}
    </div>
  );
};

export default MainScreen;
